
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { OnlinePortalApplicationDocumentRequest } from '@/models';
import {
	SbaForm,
	DynamicApplicationDocumentSetting,
	AutoFactory,
	DynamicApplicationDocumentSettingsApi,
	UsableProducts,
	Customer,
	FeatureFlagging,
	Permission,
	Document,
	LoanApplication
} from '@sageworks/jpi';

import { RouteMixin, ScaleMixin } from '@/mixins';
import { ApplicationWizardCard } from '@/components/application-wizard';
import { DocumentUploadRow, SbaFormDetails, HmdaGmiForm } from '@/components';
import BorrowerNotUploadingReasonModal from '@/components/documents/borrower-not-uploading-reason-modal/borrower-not-uploading-reason-modal.vue';

import { SbaFormUtils, ApplicationNavigationUtils } from '../../../utils';
import { fetchCustomerByIds } from '../../../logic/Customer.service';
import { DateFormatting } from '@sageworks/core-logic';
import { ToApplicationTemplateType } from '@/utils/loan-app-type-helper/LoanAppTypeHelper';
import DemographicForms from '../../../components/documents/demographic-forms/demographic-forms.vue';
import MarkOnFileBanner from '../../../components/documents/mark-on-file-banner/mark-on-file-banner.vue';
import { DetailedAggregatedDemographicInformation } from '@/models/aggregated-demographic-information';
import { isInFinalStatus as isApplicationInFinalStatus } from '../../../utils/application-final-status-helper/application-final-status-helper';
import { getCustomerNameFromCustomer as getCustomerName } from '../../../utils/customer-name-helper';

@Component({
	components: {
		DemographicForms,
		FontAwesomeIcon,
		ApplicationWizardCard,
		DocumentUploadRow,
		SbaFormDetails,
		BorrowerNotUploadingReasonModal,
		HmdaGmiForm,
		MarkOnFileBanner
	}
})
export default class Documentation extends Mixins(RouteMixin, ScaleMixin) {
	// Replicated in institution settings
	defaultDocumentsPageMessage = 'Please provide the following documents...';

	@Prop({ default: null })
	applicationId!: string;

	formDataLoaded = false;

	customers: Customer[] = [];

	documentSettings: DynamicApplicationDocumentSetting | null = {};

	shouldMatchGeneralMessage = true;

	additionalDocumentRequestId = -1;

	get demographicInformation() {
		const demographic1071EmailsEnabled = this.demographic1071EmailsEnabled;
		const showGmiForm = this.showGmiForm;
		const customers = this.customers;

		return this.directStore.getters.DemographicForm.demographicInformationByCustomers.map<DetailedAggregatedDemographicInformation>(x => {
			const customer = customers.find(c => c.id === x.customerId);
			return {
				...x,
				demographic1071Summaries: demographic1071EmailsEnabled ? x.demographic1071Summaries : [],
				gmiFormLinks: showGmiForm ? x.gmiFormLinks : [],
				customerName: this.getCustomerNameFromCustomer(customer),
				customerEmail: customer?.emailAddress ?? ''
			};
		});
	}

	get showGmiForm() {
		return this.featureFlags?.has(FeatureFlagging.FeatureFlagEnum.EnableHmdainDya) ?? false;
	}

	get demographic1071EmailsEnabled() {
		return this.featureFlags?.has(FeatureFlagging.FeatureFlagEnum.Enable1071FormEmails) ?? false;
	}

	get isOPNDocumentDetectionEnabled() {
		return this.featureFlags?.has(FeatureFlagging.FeatureFlagEnum.EnableOpnDocumentDetection) ?? false;
	}

	get documentMessage() {
		if (!this.shouldMatchGeneralMessage && this.directStore.state.UserCreatedMessages.DocumentsPageMessageHtml !== null) {
			return this.directStore.state.UserCreatedMessages.DocumentsPageMessageHtml;
		}
		return this.directStore.state.InstitutionSettings.documentsMessage.message ?? this.defaultDocumentsPageMessage;
	}

	get isLender() {
		return this.directStore.getters.User.isLender;
	}
	get canSubmitBeforeLenderReview(): boolean {
		return this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationBeforeLenderReview);
	}
	get canSubmitApplicationInBorrowerReview(): boolean {
		return this.isLender
			? this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationInBorrowerReview)
			: true;
	}

	get applicationStatus() {
		return this.directStore.state.LoanApplicationData.loanApplication?.applicationStatus ?? null;
	}
	get loanRequiresReview() {
		return this.directStore.getters.LoanApplicationMetadata.loanRequiresReview;
	}
	get isInFinalStatus(): boolean {
		return isApplicationInFinalStatus(this.applicationStatus ?? LoanApplication.ApplicationStatusEnum.New, this.canSubmitApplicationInBorrowerReview);
	}
	get finalTabTitle(): string {
		if (!this.isInFinalStatus && !this.canSubmitBeforeLenderReview && this.loanRequiresReview) {
			return 'Review';
		}
		return 'Submission';
	}

	get featureFlags() {
		return this.directStore.state.FeatureFlag.activeFeatures;
	}

	get itemLabel(): string {
		if (this.hasCommunityLendingSubscription === undefined) {
			return '';
		}
		return this.hasCommunityLendingSubscription ? 'Request Form' : 'Application';
	}

	get hasCommunityLendingSubscription(): boolean | undefined {
		return this.directStore.state.UsableProducts.usableProducts?.has('abrigoCommunityLending' as UsableProducts.ProductsEnum);
	}

	get sbaFormsByCustomerId() {
		return this.directStore.getters.SbaForm.sbaFormsByCustomers;
	}

	get applicationDocumentRequests() {
		return this.directStore.state.ApplicationDocumentRequest.documentRequests ?? {};
	}

	get customerIds() {
		return new Set(
			[...Object.keys(this.sbaFormsByCustomerId), ...this.customerIdsWithDocumentRequests, ...this.demographicInformation.map(x => x.customerId)].map(x =>
				Number(x)
			)
		);
	}

	get customerIdsWithDocumentRequests() {
		return new Set([...Object.keys(this.applicationDocumentRequests ?? {})].map(x => Number(x)));
	}

	get isLoading() {
		return !this.formDataLoaded;
	}

	get hasCustomersWithDocumentationNeeds() {
		return this.customersWithDocumentationNeeds.length > 0;
	}

	get customersWithDocumentationNeeds() {
		return this.customers.filter(x => {
			return this.sbaFormsByCustomerId[x.id] != null || this.applicationDocumentRequests[x.id] != null;
		});
	}

	get requestsWithMatches() {
		const allDocRequests = Object.values(this.applicationDocumentRequests).flat() as OnlinePortalApplicationDocumentRequest[];
		return allDocRequests.filter(x => !x.dateWaived && !x.dateUploaded && x.possibleDocuments.length > 0);
	}

	get totalMatchCount(): number {
		return this.requestsWithMatches.map(x => x.possibleDocuments.length).reduce((sum, count) => sum + count, 0);
	}

	get showMarkOnFileBanner(): boolean {
		return this.hasCustomersWithDocumentationNeeds && this.isOPNDocumentDetectionEnabled;
	}

	public async uploadOtherDocuments(customerId: number): Promise<void> {
		const { loanApplication } = this.directStore.state.LoanApplicationData;

		if (!loanApplication?.id || !customerId) {
			return;
		}
		const tempId = this.additionalDocumentRequestId--;

		const newDocumentRequest = new OnlinePortalApplicationDocumentRequest({
			id: tempId,
			customerId,
			loanApplicationId: loanApplication.id,
			dateCreated: DateFormatting.formatDateForJpi(new Date())
		});

		await this.directStore.dispatch.ApplicationDocumentRequest.addDocumentRequest(newDocumentRequest);
		document.getElementById('additional-document-select-' + tempId)?.focus();
	}
	public getCustomerNameFromCustomer(customer: Customer) {
		return getCustomerName(customer);
	}

	public canShowSbaForm(sbaForm: SbaForm) {
		if (!sbaForm.formType) return false;

		return SbaFormUtils.isFormTypeLenderOnly(sbaForm.formType) ? this.isLender : true;
	}

	public async onMarkOnFileConfirmed(data: { documentRequestId: number; selectedDocument: Document }): Promise<void> {
		const { documentRequestId } = data;

		try {
			// Update the document request to mark it as waived with "Document on file" reason
			await this.directStore.dispatch.ApplicationDocumentRequest.patchApplicationDocumentRequest({
				id: documentRequestId,
				documentRequestProperties: {
					dateWaived: DateFormatting.formatDateForJpi(new Date()),
					waivedReason: 'Document on file',
					waivedByLoanOfficerId: this.directStore.state.User.user?.id
				}
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error marking document on file:', error);
		}
	}

	// eslint-disable-next-line max-lines-per-function
	public async beforeMount(): Promise<void> {
		const loanApplicationId = Number(this.applicationId);
		if (loanApplicationId == null || Number.isNaN(loanApplicationId)) return;

		const dataLoadPromises: Promise<any>[] = [
			// TODO: the below action calls the same endpoint for the same data as line 272
			this.directStore.dispatch.LoanApplicationData.loadLoanApplication({ loanApplicationId: loanApplicationId }),
			this.directStore.dispatch.LoanApplicationMetadata.loadMetadata({ loanApplicationId: loanApplicationId, ignoreFormioProperties: true }),
			this.directStore.dispatch.ApplicationDocumentRequest.loadPagedDocumentRequests({ loanApplicationId }),
			this.directStore.dispatch.SbaForm.loadSbaForms({ loanApplicationId }),
			this.directStore.dispatch.DemographicForm.loadData({ loanApplicationId: loanApplicationId }),
			this.directStore.dispatch.FeatureFlag.fetchActiveFeatures(),
			this.directStore.dispatch.LenderAuthorizationModule.loadUserPermissions(),
			this.directStore.dispatch.UsableProducts.fetchUsableProducts(),
			this.loadApplicationDependentData(loanApplicationId)
		];
		if (this.directStore.state.DocumentName.documentNames.length === 0) {
			dataLoadPromises.push(this.directStore.dispatch.DocumentName.loadDocumentNames());
		}

		if (!this.isLender) {
			dataLoadPromises.push(this.directStore.dispatch.BorrowerAccessRestrictions.fetchCurrentUserWritableEntities());
		}

		await Promise.all(dataLoadPromises);
		// Now that we gathered all the forms and document requests, we can load the customers
		this.customers = await fetchCustomerByIds(Array.from(this.customerIds));

		// document upload depends on form metadata because it must figure out which loans to associate the
		// 	document to
		await this.directStore.dispatch.MultiLoanApplicationForm.loadAllFormData();

		this.formDataLoaded = true;
	}

	public async loadApplicationDependentData(loanApplicationId: number) {
		await this.directStore.dispatch.Application.fetchApplicationById(loanApplicationId);
		const applicationType = this.directStore.getters.Application.applicationById(loanApplicationId)?.type;

		if (applicationType != null) {
			// Load document settings
			const documentSettingsService = AutoFactory.get(DynamicApplicationDocumentSettingsApi);
			this.documentSettings = (await documentSettingsService.getByApplicationType(applicationType)) ?? {};

			// Load document messages
			await this.directStore.dispatch.InstitutionSettings.fetchShouldMatchGeneralDocumentsPageMessage({ applicationType: applicationType });
			this.shouldMatchGeneralMessage = this.directStore.getters.InstitutionSettings.shouldMatchGeneralDocumentsPageMessage;
			if (!this.shouldMatchGeneralMessage) {
				await this.directStore.dispatch.UserCreatedMessages.fetchDocumentsPageMessage(ToApplicationTemplateType(applicationType));
			}

			// Load additional allowable documents
			if (this.directStore.state.DocumentName.additionalAllowableDocuments.length === 0) {
				await this.directStore.dispatch.DocumentName.loadAdditionalAllowableDocuments(applicationType);
			}

			// Load e-sign S2 flag (show templates in application)
			await this.directStore.dispatch.ESignModule.getShowDocusignTemplatesInApplication({ applicationType: applicationType });
		}
	}

	public navigateToApplication() {
		ApplicationNavigationUtils.navigateToApplicationForm(this.$router, this.applicationId);
	}

	public navigateToSubmit() {
		ApplicationNavigationUtils.navigateToApplicationSubmit(this.$router, this.applicationId);
	}
}
